var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import React, { useRef } from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useIntl } from "react-intl";
import { COLUMN_MIN_WIDTH } from "../utils/constants";
import css from "./ColumnHeader.module.css";
/**
 * Interactive column header rendered inside a stripes MultiColumnList header cell. Provides:
 * - drag-to-reorder via @dnd-kit (the label area is the drag handle), and
 * - drag-to-resize via a handle pinned to the right edge of the header cell.
 *
 * The resize handle is absolutely positioned against the surrounding `[role="columnheader"]`
 * cell (made `position: relative` in TableComponent.module.css), independent of MCL internals.
 * Pointer capture keeps resize events flowing even when the cursor leaves the thin handle.
 */
export var ColumnHeader = function (_a) {
    var id = _a.id, label = _a.label, onResize = _a.onResize;
    var formatMessage = useIntl().formatMessage;
    // Only `listeners` are spread onto the label: the header cell already provides button
    // semantics/focus for sorting, so spreading @dnd-kit `attributes` (role=button, tabIndex)
    // would create a nested, conflicting control. Reorder is pointer-driven only.
    var _b = useSortable({ id: id }), listeners = _b.listeners, setNodeRef = _b.setNodeRef, transform = _b.transform, isDragging = _b.isDragging;
    var resizeState = useRef(null);
    var style = {
        transform: CSS.Translate.toString(transform),
        opacity: isDragging ? 0.5 : undefined,
    };
    var handleResizePointerDown = function (event) {
        // Never let a resize start a column drag or trigger the sort click.
        event.stopPropagation();
        event.preventDefault();
        var headerCell = event.currentTarget.closest('[role="columnheader"]');
        var startWidth = headerCell
            ? headerCell.getBoundingClientRect().width
            : COLUMN_MIN_WIDTH;
        resizeState.current = { startX: event.clientX, startWidth: startWidth };
        event.currentTarget.setPointerCapture(event.pointerId);
    };
    var handleResizePointerMove = function (event) {
        if (!resizeState.current)
            return;
        var delta = event.clientX - resizeState.current.startX;
        onResize(id, resizeState.current.startWidth + delta);
    };
    var handleResizePointerUp = function (event) {
        var _a, _b;
        if (!resizeState.current)
            return;
        resizeState.current = null;
        (_b = (_a = event.currentTarget).releasePointerCapture) === null || _b === void 0 ? void 0 : _b.call(_a, event.pointerId);
    };
    var handleResizeKeyDown = function (event) {
        var step = event.shiftKey ? 24 : 8;
        var delta = 0;
        if (event.key === "ArrowLeft")
            delta = -step;
        else if (event.key === "ArrowRight")
            delta = step;
        else
            return;
        event.preventDefault();
        event.stopPropagation();
        var headerCell = event.currentTarget.closest('[role="columnheader"]');
        var current = headerCell
            ? headerCell.getBoundingClientRect().width
            : COLUMN_MIN_WIDTH;
        onResize(id, current + delta);
    };
    return (React.createElement("span", { ref: setNodeRef, className: css.header, style: style },
        React.createElement("span", __assign({ className: css.label }, listeners), label),
        React.createElement("span", { className: css.resizeHandle, onPointerDown: handleResizePointerDown, onPointerMove: handleResizePointerMove, onPointerUp: handleResizePointerUp, onKeyDown: handleResizeKeyDown, onClick: function (event) { return event.stopPropagation(); }, role: "separator", tabIndex: 0, "aria-orientation": "vertical", "aria-label": formatMessage({
                id: "ui-br-agency.table.columns.resize",
            }) })));
};
